import { DashboardWidget } from '@treasury/domain/dashboard/dashboard-widget.entity';
import { TmBaseComponent } from '@treasury/presentation';
import { closeIcon, gripDotsIcon } from '@treasury/presentation/assets/icons';
import '@vaadin/grid';
// eslint-disable-next-line no-duplicate-imports
import type { GridDragStartEvent, GridDropEvent } from '@vaadin/grid';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'order-widgets-table';

@customElement(tagName)
export class OrderWidgetsTable extends TmBaseComponent {
    @property({ type: Array })
    public configuredWidgets: DashboardWidget[] = [];

    @property({ type: Boolean })
    public isDesktop = true;

    @state()
    private draggedItem: DashboardWidget | undefined;

    protected render() {
        if (this.configuredWidgets.length === 0) {
            return html`<div class="text-center text-secondary">No widgets configured</div>`;
        }
        return html`<vaadin-grid
            all-rows-visible
            rows-draggable
            .items=${this.configuredWidgets}
            .dropMode=${this.draggedItem ? 'between' : undefined}
            @grid-dragstart="${(event: GridDragStartEvent<DashboardWidget>) => {
                [this.draggedItem] = event.detail.draggedItems;
            }}"
            @grid-dragend="${() => {
                this.draggedItem = undefined;
            }}"
            @grid-drop="${(event: GridDropEvent<DashboardWidget>) => {
                const { dropTargetItem, dropLocation } = event.detail;
                // Only update when dropping on another item
                if (this.draggedItem && dropTargetItem !== this.draggedItem) {
                    const draggedItemIndex = this.configuredWidgets.indexOf(this.draggedItem);
                    this.configuredWidgets.splice(draggedItemIndex, 1);
                    const dropIndex =
                        this.configuredWidgets.indexOf(dropTargetItem) +
                        (dropLocation === 'below' ? 1 : 0);
                    this.configuredWidgets.splice(dropIndex, 0, this.draggedItem);
                    this.configuredWidgets = [...this.configuredWidgets];
                    const previousWidget = this.configuredWidgets[dropIndex - 1];
                    const nextWidget = this.configuredWidgets[dropIndex + 1];

                    if (this.isDesktop) {
                        if (!previousWidget) {
                            this.draggedItem.left = 0;
                            this.draggedItem.top = this.configuredWidgets[0].top - 1;
                        }
                        if (nextWidget) {
                            this.draggedItem.top = nextWidget.top - 1;
                        }
                        if (!nextWidget && previousWidget) {
                            this.draggedItem.top = previousWidget.top + 1;
                        }
                        if (previousWidget?.left > this.draggedItem.left) {
                            this.draggedItem.left = previousWidget.left;
                        }
                    } else {
                        this.configuredWidgets.forEach((widget, index) => {
                            widget.top = index + 1;
                        });
                    }
                    this.dispatchEvent(
                        new CustomEvent('widgets-reordered', { detail: this.configuredWidgets })
                    );
                }
            }}"
            ><vaadin-grid-column
                width="2rem"
                flex-grow="0"
                ${columnBodyRenderer(this.renderHandle, [])}
            ></vaadin-grid-column
            ><vaadin-grid-column
                auto-width
                ${columnBodyRenderer(this.renderTitle, [])}
            ></vaadin-grid-column
            ><vaadin-grid-column
                width="4rem"
                flex-grow="0"
                text-align="end"
                ${columnBodyRenderer(this.renderRemove, [])}
            ></vaadin-grid-column
        ></vaadin-grid>`;
    }

    private isRequired(widget: DashboardWidget) {
        return widget.isRequired || widget.title === 'Accounts';
    }

    private renderHandle() {
        return html`<div><tm-icon .icon=${gripDotsIcon} color="#70757A"></tm-icon></div>`;
    }

    private renderTitle(widget: DashboardWidget) {
        return html`<div class="text-start h-[55px] flex flex-col items-start justify-center">
            <span>${widget.title}</span>
            <span class="text-xs text-secondary">(${widget.description})</span>
        </div>`;
    }

    private renderRemove(widget: DashboardWidget) {
        if (this.isRequired(widget)) {
            return nothing;
        }
        return html`<div class="text-end">
            <tm-button
                importance="tertiary"
                @click=${() => {
                    const index = this.configuredWidgets.indexOf(widget);
                    widget.isRemoved = true;
                    this.configuredWidgets.splice(index, 1);
                    this.configuredWidgets = [...this.configuredWidgets];
                    this.dispatchEvent(
                        new CustomEvent('widget-removed', {
                            detail: { widget, configuredWidgets: this.configuredWidgets },
                        })
                    );
                }}
                ><tm-icon .icon=${closeIcon} color="#70757A"></tm-icon
            ></tm-button>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: OrderWidgetsTable;
    }
}
